import { Check, Copy } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { useClipboard } from "@/hooks/use-clipboard"
import { cn } from "@/lib/utils"

/**
 * OKS-21 — Copy invite URL button.
 *
 * Primary action on desktop (icon + label, primary variant). Reuses
 * the existing OKS-16 `useClipboard` hook (no new clipboard utility).
 * On success, fires a sonner success toast (data-testid
 * `toast-invite-copied` exposed via the toast `id`) and flips the
 * button label to "Copied ✓" for 1.5s before reverting.
 *
 * **Sonner testid contract:** sonner exposes a per-toast `data-sonner-toast`
 * attribute. We pass the `id` option as the testid string so QA's
 * Playwright + Vitest specs can target via
 * `[data-sonner-toast][data-id="toast-invite-copied"]`. In tests,
 * sonner is typically mocked at module level, so `toast.success` is a
 * vi.fn — the spec asserts the call args.
 *
 * Failure path: surfaces `invites.panel.toast.copy_failed` toast and
 * does NOT flip the label (user retains the option to copy again).
 */

interface InviteCopyButtonProps {
  url: string
  /** Tailwind className override (e.g. for full-width sticky on mobile). */
  className?: string
  /** Variant override — defaults to "default" (primary). Mobile usage
   *  passes "outline" since Share is the mobile primary. */
  variant?: "default" | "outline" | "secondary"
  /** Override testid. Defaults to `invites-panel-copy-button` per UX Spec §6. */
  testId?: string
}

export function InviteCopyButton({
  url,
  className,
  variant = "default",
  testId = "invites-panel-copy-button",
}: InviteCopyButtonProps) {
  const { t } = useTranslation()
  const [, copy] = useClipboard()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const ok = await copy(url)
    if (ok) {
      toast.success(t("invites.panel.toast.copied"), {
        id: "toast-invite-copied",
      })
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } else {
      toast.error(t("invites.panel.toast.copy_failed"))
    }
  }

  const Icon = copied ? Check : Copy
  const label = copied
    ? t("invites.panel.actions.copied")
    : t("invites.panel.actions.copy")

  return (
    <Button
      type="button"
      variant={variant}
      onClick={() => {
        void handleCopy()
      }}
      data-testid={testId}
      data-state={copied ? "copied" : "idle"}
      aria-label={label}
      className={cn(className)}
    >
      <Icon className="size-4" aria-hidden="true" />
      {label}
    </Button>
  )
}
